import type { WidgetType } from "WidgetProvider/factory/types";
import { generateDragStateForAnvilLayout } from "layoutSystems/anvil/utils/widgetUtils";
import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { SelectionRequestType } from "sagas/WidgetSelectUtils";
import { getShouldAllowDrag } from "selectors/widgetDragSelectors";
import { isWidgetFocused, isWidgetSelected } from "selectors/widgetSelectors";
import { useWidgetDragResize } from "utils/hooks/dragResizeHooks";
import { useWidgetSelection } from "utils/hooks/useWidgetSelection";

/**
 * Touch drag initiation (M3.4):
 * Mouse drags initiate via the native `dragstart` event (see below), which
 * never fires for touch interactions. On touch, a drag is initiated by a
 * long-press: hold still for the delay below, then move. A finger that moves
 * beyond the slop before the delay elapses is treated as a scroll and the
 * pending drag is abandoned.
 */
export const ANVIL_TOUCH_DRAG_INITIATION_DELAY_MS = 300;
export const ANVIL_TOUCH_DRAG_SLOP_PX = 10;

export const useAnvilWidgetDrag = (
  widgetId: string,
  widgetType: WidgetType,
  layoutId: string,
  ref: React.RefObject<HTMLDivElement>, // Ref object to reference the AnvilFlexComponent
) => {
  // Retrieve state from the Redux store
  const isSelected = useSelector(isWidgetSelected(widgetId));
  const isFocused = useSelector(isWidgetFocused(widgetId));
  const shouldAllowDrag = useSelector(getShouldAllowDrag);
  const { selectWidget } = useWidgetSelection();
  const generateDragState = useCallback(() => {
    return generateDragStateForAnvilLayout({
      widgetType,
      layoutId,
    });
  }, [layoutId]);
  const { setDraggingState } = useWidgetDragResize();

  // Callback function for handling drag start events
  const onDragStart = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (shouldAllowDrag && ref.current && !(e.metaKey || e.ctrlKey)) {
        if (!isFocused) return;

        if (!isSelected) {
          // Select the widget if not already selected
          selectWidget(SelectionRequestType.One, [widgetId]);
        }

        // Generate and set the dragging state for the Anvil layout
        const draggingState = generateDragState();

        setDraggingState(draggingState);
      }
    },
    [
      shouldAllowDrag,
      isFocused,
      isSelected,
      selectWidget,
      widgetId,
      generateDragState,
      setDraggingState,
    ],
  );

  /**
   * Touch (and pen) drag initiation via Pointer Events.
   *
   * Contract differences from the mouse path, by design:
   * - No `isFocused` gate: focus is hover-driven and hover does not exist on
   *   touch. The long-press itself expresses the intent the hover gate
   *   verifies for mouse.
   * - Scroll-vs-drag disambiguation: a long-press (hold still) starts a drag;
   *   movement beyond the slop first lets the browser scroll as usual.
   *
   * Once a touch drag starts, page scrolling is suppressed by calling
   * preventDefault on document touchmove events (registered non-passive at
   * pointerdown, i.e. before any scroll can begin). The long-press context
   * menu is suppressed while a touch drag is pending or active.
   */
  const startTouchDrag = useCallback(() => {
    if (shouldAllowDrag && ref.current) {
      if (!isSelected) {
        selectWidget(SelectionRequestType.One, [widgetId]);
      }

      setDraggingState(generateDragState());
    }
  }, [
    shouldAllowDrag,
    isSelected,
    selectWidget,
    widgetId,
    generateDragState,
    setDraggingState,
  ]);
  // Latest-ref so pointer listeners stay stable across re-renders and a
  // re-render cannot cancel an in-flight long-press.
  const startTouchDragRef = useRef(startTouchDrag);

  useEffect(
    function syncLatestStartTouchDrag() {
      startTouchDragRef.current = startTouchDrag;
    },
    [startTouchDrag],
  );

  useEffect(
    function attachTouchDragInitiationListeners() {
      const element = ref.current;

      if (!element || !shouldAllowDrag) return;

      let longPressTimer: number | undefined;
      let activePointerId: number | null = null;
      let touchDragActive = false;
      let startX = 0;
      let startY = 0;

      const suppressTouchScroll = (e: TouchEvent) => {
        if (touchDragActive) {
          e.preventDefault();
        }
      };
      const suppressContextMenu = (e: Event) => {
        if (longPressTimer !== undefined || touchDragActive) {
          e.preventDefault();
        }
      };
      const clearPendingLongPress = () => {
        if (longPressTimer !== undefined) {
          window.clearTimeout(longPressTimer);
          longPressTimer = undefined;
        }
      };
      const onPointerDown = (e: PointerEvent) => {
        // Mouse drags initiate via the native dragstart path.
        if (e.pointerType === "mouse" || !e.isPrimary) return;

        activePointerId = e.pointerId;
        startX = e.clientX;
        startY = e.clientY;
        document.addEventListener("touchmove", suppressTouchScroll, {
          passive: false,
        });
        longPressTimer = window.setTimeout(() => {
          longPressTimer = undefined;
          touchDragActive = true;
          startTouchDragRef.current();
        }, ANVIL_TOUCH_DRAG_INITIATION_DELAY_MS);
      };
      const onPointerMove = (e: PointerEvent) => {
        if (e.pointerId !== activePointerId || longPressTimer === undefined) {
          return;
        }

        const distance = Math.hypot(e.clientX - startX, e.clientY - startY);

        if (distance > ANVIL_TOUCH_DRAG_SLOP_PX) {
          // Finger moved before the long-press elapsed: it is a scroll.
          clearPendingLongPress();
          document.removeEventListener("touchmove", suppressTouchScroll);
          activePointerId = null;
        }
      };
      const onPointerEnd = (e: PointerEvent) => {
        if (e.pointerId !== activePointerId) return;

        clearPendingLongPress();
        touchDragActive = false;
        document.removeEventListener("touchmove", suppressTouchScroll);
        activePointerId = null;
      };

      element.addEventListener("pointerdown", onPointerDown);
      element.addEventListener("pointermove", onPointerMove);
      element.addEventListener("pointerup", onPointerEnd);
      element.addEventListener("pointercancel", onPointerEnd);
      element.addEventListener("contextmenu", suppressContextMenu);

      return () => {
        clearPendingLongPress();
        document.removeEventListener("touchmove", suppressTouchScroll);
        element.removeEventListener("pointerdown", onPointerDown);
        element.removeEventListener("pointermove", onPointerMove);
        element.removeEventListener("pointerup", onPointerEnd);
        element.removeEventListener("pointercancel", onPointerEnd);
        element.removeEventListener("contextmenu", suppressContextMenu);
      };
    },
    [shouldAllowDrag],
  );

  // Effect hook to add and remove drag start event listeners
  useEffect(() => {
    if (ref.current) {
      // Configure the draggable attribute and cursor style based on drag permission
      ref.current.draggable = shouldAllowDrag;
      ref.current.style.cursor = shouldAllowDrag ? "grab" : "default";

      // Add drag start event listener
      ref.current.addEventListener("dragstart", onDragStart);
    }

    // Clean up event listeners when the component unmounts
    return () => {
      if (ref.current) {
        ref.current.removeEventListener("dragstart", onDragStart);
      }
    };
  }, [onDragStart, shouldAllowDrag]);
};
